/**
 * Owns the map-tool state in MPV: which interaction tool is active
 * (analyzePolygon, measureDistance, etc.), which settings tool is active
 * (manageLayers, etc.), and whether the Analyze Area popup is open. Plus
 * the cleanup-cascade actions (`resetAndCloseAA`, `openAnalyzePolygonPopup`)
 * and the analytics + auto-cleanup effects that orchestrate them.
 */

import React from 'react';

import {SidebarViewState} from 'app/components/TabbedSidebar/TabbedSidebar';
import {PolygonDispatch, PolygonFeature} from 'app/providers/MapPolygonStateProvider';
import {NotesActions} from 'app/stores/NotesStore';
import {recordEvent} from 'app/tools/Analytics';
import {usePrevious} from 'app/utils/hookUtils';

import {MapInteractionTool, MapSettingsTool} from '../view';

const ANALYZE_SIDEBAR_TITLE = 'Analysis';

interface Args {
  polygonDispatch: PolygonDispatch;
  notesActions: NotesActions;
  tabbedSidebarState: SidebarViewState;
  setTabbedSidebarState: (
    state: SidebarViewState | ((prev: SidebarViewState) => SidebarViewState)
  ) => void;
}

export interface AnalyzeAreaTool {
  activeMapInteractionTool: MapInteractionTool | null;
  setActiveMapInteractionTool: React.Dispatch<React.SetStateAction<MapInteractionTool | null>>;
  activeMapSettingsTool: MapSettingsTool | null;
  setActiveMapSettingsTool: React.Dispatch<React.SetStateAction<MapSettingsTool | null>>;
  isAnalyzeAreaPopupOpen: boolean;
  setIsAnalyzeAreaPopupOpen: React.Dispatch<React.SetStateAction<boolean>>;
  resetAndCloseAA: () => void;
  openAnalyzePolygonPopup: (polygonFeature: PolygonFeature) => void;
}

export default function useAnalyzeAreaTool({
  polygonDispatch,
  notesActions,
  tabbedSidebarState,
  setTabbedSidebarState,
}: Args): AnalyzeAreaTool {
  const [activeMapInteractionTool, setActiveMapInteractionTool] =
    React.useState<MapInteractionTool | null>(null);
  const [activeMapSettingsTool, setActiveMapSettingsTool] = React.useState<MapSettingsTool | null>(
    null
  );
  const [isAnalyzeAreaPopupOpen, setIsAnalyzeAreaPopupOpen] = React.useState<boolean>(false);

  const prevActiveMapInteractionTool = usePrevious(activeMapInteractionTool);

  const resetAndCloseAA = React.useCallback(() => {
    // Remove any AA polygons from the map
    polygonDispatch({type: 'setPolygonFeature', polygonFeature: null});

    // Switch the AA draw mode back to its default
    polygonDispatch({
      type: 'setAnalyzeAreaDrawMode',
      analyzeAreaDrawMode: 'drawPolygon',
    });

    // Remove any polygons hanging out from pending notes
    notesActions.removePendingNoteGeometryFeatureById('pending-note-location-feature');

    // If the AA sidebar is selected when we reset and close, and reset the sidebar state so
    // AA its no longer active and close popup.
    if (tabbedSidebarState.currentViewTitle === ANALYZE_SIDEBAR_TITLE) {
      setTabbedSidebarState({currentViewTitle: null, isExpanded: false});
      setIsAnalyzeAreaPopupOpen(false);
    } else {
      // Otherwise, if another sidebar mode is selected, just close the popup.
      setIsAnalyzeAreaPopupOpen(false);
    }

    // Most of the time when we're closing AA, we want to set activeMapInteractionTool to null. The
    // exception is if we're clearing out AA because we've opened another tool — then we want to
    // maintain that.
    if (activeMapInteractionTool === 'analyzePolygon') {
      setActiveMapInteractionTool(null);
    }
  }, [
    polygonDispatch,
    notesActions,
    tabbedSidebarState.currentViewTitle,
    setTabbedSidebarState,
    activeMapInteractionTool,
  ]);

  function openAnalyzePolygonPopup(polygonFeature: PolygonFeature) {
    polygonDispatch({type: 'setPolygonFeature', polygonFeature});
    setTabbedSidebarState({
      isExpanded: true,
      currentViewTitle: ANALYZE_SIDEBAR_TITLE,
    });
  }

  // When switching away from the analyzePolygon tool, close AA.
  React.useEffect(() => {
    if (
      prevActiveMapInteractionTool === 'analyzePolygon' &&
      activeMapInteractionTool !== 'analyzePolygon'
    ) {
      resetAndCloseAA();
    }
  }, [activeMapInteractionTool, prevActiveMapInteractionTool, resetAndCloseAA]);

  // Close AA when the Analysis sidebar tab transitions from active to
  // inactive (tab switch or sidebar collapse). The effect fires after
  // tabbedSidebarState has updated, so resetAndCloseAA reads the new
  // currentViewTitle and skips its tab-switching branch.
  const prevSidebarState = usePrevious(tabbedSidebarState);
  React.useEffect(() => {
    if (!prevSidebarState) return;
    const wasActive =
      prevSidebarState.currentViewTitle === ANALYZE_SIDEBAR_TITLE && prevSidebarState.isExpanded;
    const isActive =
      tabbedSidebarState.currentViewTitle === ANALYZE_SIDEBAR_TITLE &&
      tabbedSidebarState.isExpanded;
    if (wasActive && !isActive) {
      resetAndCloseAA();
    }
  }, [tabbedSidebarState, prevSidebarState, resetAndCloseAA]);

  // Analytics: record events when tools are activated
  React.useEffect(() => {
    if (activeMapInteractionTool === 'analyzePolygon') {
      recordEvent('Opened Analysis');
    } else if (activeMapInteractionTool === 'measureDistance') {
      recordEvent('Opened measure distance');
    }
  }, [activeMapInteractionTool]);

  return {
    activeMapInteractionTool,
    setActiveMapInteractionTool,
    activeMapSettingsTool,
    setActiveMapSettingsTool,
    isAnalyzeAreaPopupOpen,
    setIsAnalyzeAreaPopupOpen,
    resetAndCloseAA,
    openAnalyzePolygonPopup,
  };
}
